/**
 * MinistrareIA - WhatsApp Business Chat List Component
 * Padronizado com WhatsApp Pessoal
 * 
 * Diferenças de estrutura de dados (mantidas internamente):
 * - chat.id instead of chat.remoteJid
 * - chat.contact_name instead of chat.pushName
 * - chat.contact_phone for phone number
 * - chat.last_message_text for preview
 * - chat.last_message_at for timestamp
 * - chat.unread_count for unread badge
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Search, User, Archive, Check, CheckCheck,
  Image, Mic, Video, FileText, ChevronDown, Camera, Paperclip
} from 'lucide-react';
import { useTheme } from '../../contexts/ThemeContext';

/**
 * Robustly parse timestamp from various formats
 */
const parseTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return isNaN(timestamp.getTime()) ? null : timestamp;
  if (typeof timestamp === 'number') {
    const ms = timestamp < 10000000000 ? timestamp * 1000 : timestamp;
    const date = new Date(ms);
    return isNaN(date.getTime()) ? null : date;
  }
  if (typeof timestamp === 'string') {
    let date = new Date(timestamp);
    if (!isNaN(date.getTime())) return date;
    const num = Number(timestamp);
    if (!isNaN(num)) {
      const ms = num < 10000000000 ? num * 1000 : num;
      date = new Date(ms);
      if (!isNaN(date.getTime())) return date;
    }
    date = new Date(timestamp.replace(' ', 'T'));
    if (!isNaN(date.getTime())) return date;
  }
  return null;
};

const ChatList = ({ 
  chats, 
  selectedChat, 
  onSelectChat, 
  loading,
  onLoadMore,
  hasMore = false 
}) => {
  const { colors } = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [hoveredChat, setHoveredChat] = useState(null);
  const [searchFocused, setSearchFocused] = useState(false);
  const listRef = useRef(null);
  const searchInputRef = useRef(null);

  // Keyboard shortcut for search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
        e.preventDefault();
        searchInputRef.current?.focus();
      }
      if (e.key === 'Escape' && searchTerm) {
        setSearchTerm('');
        searchInputRef.current?.blur();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [searchTerm]);

  // Filter and search chats
  const filteredChats = useMemo(() => {
    let result = chats || [];

    // Apply filters
    if (filter === 'unread') {
      result = result.filter(chat => chat.unread_count > 0);
    }

    // Apply search
    if (searchTerm) {
      const term = searchTerm.toLowerCase().trim();
      result = result.filter(chat => {
        const name = (chat.contact_name || '').toLowerCase();
        const phone = (chat.contact_phone || '').toLowerCase();
        const lastMsg = (chat.last_message_text || '').toLowerCase();
        
        return name.includes(term) || phone.includes(term) || lastMsg.includes(term);
      });
    }

    return result;
  }, [chats, filter, searchTerm]);

  // Format time for display
  const formatChatTime = useCallback((timestamp) => {
    if (!timestamp) return '';

    const date = parseTimestamp(timestamp);
    if (!date) return '';

    const now = new Date();
    const diffDays = Math.floor((now - date) / (1000 * 60 * 60 * 24));

    if (diffDays === 0) {
      return date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Ontem';
    } else if (diffDays < 7) {
      return date.toLocaleDateString('pt-BR', { weekday: 'short' });
    } else {
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });
    }
  }, []);

  // Get display name
  const getDisplayName = useCallback((chat) => {
    return chat.contact_name || chat.contact_phone || 'Desconhecido';
  }, []);

  // Media type config for preview icons
  const mediaConfig = useMemo(() => ({
    image: { icon: Camera, label: 'Foto' },
    video: { icon: Video, label: 'Vídeo' },
    audio: { icon: Mic, label: 'Áudio' },
    document: { icon: FileText, label: 'Documento' },
  }), []);

  // Get message preview (returns { text, icon } or just { text })
  const getPreview = useCallback((chat) => {
    const text = chat.last_message_text || '';
    const media = mediaConfig[text];
    if (media) {
      return { text: media.label, Icon: media.icon, isMedia: true };
    }
    return { text, Icon: null, isMedia: false };
  }, [mediaConfig]);

  // Get message status icon (for sent messages)
  const getMessageStatus = useCallback((chat) => {
    if (!chat.last_message_from_me) return null;
    
    return <CheckCheck size={16} style={{ color: colors.textTertiary, flexShrink: 0 }} />;
  }, [colors]);

  // Check if chat is selected
  const isChatSelected = useCallback((chat) => {
    return selectedChat && selectedChat.id === chat.id;
  }, [selectedChat]);

  // Filter options - Simplificado: apenas Todas e Não lidas
  const filterOptions = [
    { id: 'all', label: 'Tudo' },
    { id: 'unread', label: 'Não lidas' },
  ];

  // Styles - Padronizado com WhatsApp Pessoal (cores verdes)
  const styles = {
    container: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
      backgroundColor: colors.bgPrimary,
    },
    header: {
      padding: '10px 14px',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    searchWrapper: {
      display: 'flex',
      alignItems: 'center',
      backgroundColor: colors.bgTertiary,
      borderRadius: '8px',
      padding: '0 12px',
      marginBottom: '10px',
      transition: 'box-shadow 0.2s',
      ...(searchFocused && { boxShadow: `0 0 0 2px ${colors.accentPrimary}40` }),
    },
    searchInput: {
      flex: 1,
      border: 'none',
      background: 'transparent',
      padding: '10px 12px',
      fontSize: '15px',
      color: colors.textPrimary,
      outline: 'none',
    },
    filtersContainer: {
      display: 'flex',
      gap: '6px',
      overflowX: 'auto',
      paddingBottom: '2px',
    },
    filterBtn: {
      padding: '7px 14px',
      border: 'none',
      backgroundColor: colors.bgTertiary,
      color: colors.textSecondary,
      borderRadius: '18px',
      fontSize: '14px',
      fontWeight: 500,
      cursor: 'pointer',
      transition: 'all 0.2s',
      whiteSpace: 'nowrap',
    },
    filterBtnActive: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
    },
    archiveRow: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      padding: '12px 20px',
      cursor: 'pointer',
      borderBottom: `1px solid ${colors.borderColor}`,
    },
    listContainer: {
      flex: 1,
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    chatItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
      cursor: 'pointer',
      transition: 'background-color 0.1s',
    },
    avatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      overflow: 'hidden',
      marginRight: '12px',
      flexShrink: 0,
    },
    avatarPlaceholder: {
      width: '100%',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: colors.bgTertiary,
      color: colors.textTertiary,
    },
    chatInfo: {
      flex: 1,
      minWidth: 0,
      paddingBottom: '10px',
    },
    chatHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '4px',
    },
    chatName: {
      fontWeight: 500,
      fontSize: '17px',
      color: colors.textPrimary,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      maxWidth: '70%',
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
    },
    chatTime: {
      fontSize: '12px',
      color: colors.textTertiary,
      flexShrink: 0,
    },
    chatPreview: {
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
      fontSize: '14px',
      color: colors.textSecondary,
    },
    chatPreviewText: {
      flex: 1,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    chatBadge: {
      backgroundColor: colors.accentPrimary,
      color: 'white',
      fontSize: '11px',
      fontWeight: 600,
      minWidth: '20px',
      height: '20px',
      padding: '0 6px',
      borderRadius: '10px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      marginLeft: '8px',
    },
    emptyState: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 24px',
      color: colors.textTertiary,
      textAlign: 'center',
    },
    skeleton: {
      display: 'flex',
      alignItems: 'center',
      padding: '10px 14px',
    },
    skeletonAvatar: {
      width: '50px',
      height: '50px',
      borderRadius: '50%',
      backgroundColor: colors.bgTertiary,
      marginRight: '12px',
      animation: 'pulse 1.5s infinite',
    },
    skeletonLine: {
      height: '14px',
      borderRadius: '4px',
      backgroundColor: colors.bgTertiary,
      marginBottom: '8px',
      animation: 'pulse 1.5s infinite',
    },
  };

  return (
    <div style={styles.container}>
      {/* Header with Search and Filters */}
      <div style={styles.header}>
        <div style={styles.searchWrapper}>
          <Search 
            size={18} 
            style={{ color: searchFocused ? colors.accentPrimary : colors.textTertiary }} 
          />
          <input
            ref={searchInputRef}
            type="text"
            placeholder="Pesquisar ou começar nova conversa"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
            style={styles.searchInput}
          />
          {searchTerm && (
            <button
              style={{ padding: '4px', border: 'none', background: 'transparent', color: colors.textTertiary, cursor: 'pointer' }}
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>

        <div style={styles.filtersContainer}>
          {filterOptions.map((f) => (
            <button
              key={f.id}
              style={{
                ...styles.filterBtn,
                ...(filter === f.id ? styles.filterBtnActive : {}),
              }}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Results count when searching */}
      {searchTerm && (
        <div style={{ padding: '8px 14px', fontSize: '12px', color: colors.textTertiary, borderBottom: `1px solid ${colors.borderColor}` }}>
          {filteredChats.length} conversa{filteredChats.length !== 1 ? 's' : ''} encontrada{filteredChats.length !== 1 ? 's' : ''}
        </div>
      )}

      {/* Chat Items */}
      <div ref={listRef} style={styles.listContainer}>
        {loading ? (
          // Skeleton loading
          [...Array(6)].map((_, i) => (
            <div key={i} style={styles.skeleton}>
              <div style={styles.skeletonAvatar} />
              <div style={{ flex: 1 }}>
                <div style={{ ...styles.skeletonLine, width: '65%' }} />
                <div style={{ ...styles.skeletonLine, width: '45%', height: '12px' }} />
              </div>
            </div>
          ))
        ) : filteredChats.length === 0 ? (
          // Empty state
          <div style={styles.emptyState}>
            <div style={{ width: 80, height: 80, backgroundColor: colors.bgTertiary, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 16 }}>
              <Search size={32} />
            </div>
            <p style={{ fontSize: 16, fontWeight: 500, color: colors.textPrimary, marginBottom: 8 }}>
              {searchTerm ? 'Nenhuma conversa encontrada' : 'Nenhuma conversa'}
            </p>
            <p style={{ fontSize: 14 }}>
              {searchTerm ? 'Tente buscar por outro nome ou número' : 'Suas conversas aparecerão aqui'}
            </p>
          </div>
        ) : (
          // Chat list
          filteredChats.map((chat) => {
            const isSelected = isChatSelected(chat);
            const isHovered = hoveredChat === chat.id;
            const displayName = getDisplayName(chat);
            const preview = getPreview(chat);
            const hasUnread = chat.unread_count > 0;

            return (
              <div
                key={chat.id}
                style={{
                  ...styles.chatItem,
                  backgroundColor: isSelected ? colors.bgTertiary : isHovered ? colors.bgSecondary : 'transparent',
                }}
                onClick={() => onSelectChat(chat)}
                onMouseEnter={() => setHoveredChat(chat.id)}
                onMouseLeave={() => setHoveredChat(null)}
              >
                {/* Avatar */}
                <div style={styles.avatar}>
                  {chat.contact_profile_picture_url ? (
                    <img 
                      src={chat.contact_profile_picture_url} 
                      alt="" 
                      style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      onError={(e) => { e.target.style.display = 'none'; }}
                    />
                  ) : (
                    <div style={styles.avatarPlaceholder}>
                      <User size={24} />
                    </div>
                  )}
                </div>
                
                {/* Chat Info */}
                <div style={styles.chatInfo}>
                  <div style={styles.chatHeader}>
                    <span style={styles.chatName}>
                      {displayName}
                    </span>
                    <span style={{ ...styles.chatTime, ...(hasUnread && { color: colors.accentPrimary, fontWeight: 500 }) }}>
                      {formatChatTime(chat.last_message_at)}
                    </span>
                  </div>
                  
                  <div style={styles.chatPreview}>
                    {chat.last_message_from_me && getMessageStatus(chat)}
                    {preview.Icon && (
                      <preview.Icon size={16} style={{ flexShrink: 0, color: colors.textTertiary }} />
                    )}
                    <span style={{
                      ...styles.chatPreviewText,
                      ...(preview.isMedia ? { color: colors.textTertiary } : {}),
                    }}>
                      {preview.text || 'Clique para ver a conversa'}
                    </span>
                    
                    {hasUnread && (
                      <div style={styles.chatBadge}>
                        {chat.unread_count > 99 ? '99+' : chat.unread_count}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
        
        {/* Load more button */}
        {hasMore && (
          <div 
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, padding: 12, color: colors.accentPrimary, cursor: 'pointer', fontSize: 14, fontWeight: 500 }}
            onClick={onLoadMore}
          >
            <ChevronDown size={18} />
            Carregar mais conversas
          </div>
        )}
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        input::placeholder { color: ${colors.textTertiary}; }
      `}</style>
    </div>
  );
};

export default ChatList;